import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PreLoader from '../Assets/icons/PreLoader/PreLoader';
import {
    addFavouriteStation,
    getFavouriteSummary,
    removeFavouriteStation,
} from '../Views/AfterAuth/FavouriteStationsView/redux/favouriteStationSlice';


const TOGGLE_DEBOUNCE_MS = 400;

export const useFavouriteToggle = ({ applyLocal, notify = true, onCommitted } = {}) => {
    const dispatch = useDispatch();
    // stationId -> { timer, baseline, desired }. `baseline` is the server-true
    // state the burst started from, i.e. where a failure has to land us.
    const pending = useRef({});
    // Callbacks are read through a ref so a queued commit always runs the
    // latest ones, and so `commit`/`toggleFavourite` keep a stable identity no
    // matter what the caller passes in.
    const options = useRef({ applyLocal, notify, onCommitted });
    options.current = { applyLocal, notify, onCommitted };

    const commit = useCallback(
        async (stationId) => {
            const entry = pending.current[stationId];
            if (!entry) return;
            clearTimeout(entry.timer);
            delete pending.current[stationId];

            const { baseline, desired } = entry;
            // Tapped back to where it started — nothing to tell the server.
            if (baseline === desired) return;

            const { applyLocal: paint, notify: shouldNotify, onCommitted: done } =
                options.current;
            const thunk = desired ? addFavouriteStation : removeFavouriteStation;
            const result = await dispatch(thunk({ charging_station_id: stationId }));

            if (thunk.rejected.match(result)) {
                // A newer burst is already queued for this station; let it win
                // instead of stamping a stale state over it.
                if (!pending.current[stationId] && paint) {
                    paint(stationId, baseline);
                }
                toast.error(result.payload || 'Could not update your favourites');
                return;
            }

            // Keep the dashboard card honest after every real change.
            dispatch(getFavouriteSummary());
            if (shouldNotify) {
                toast.success(
                    desired ? 'Saved to Favourites' : 'Removed from Favourites'
                );
            }
            if (done) done(stationId, desired);
        },
        [dispatch]
    );

    // Leaving the screen inside the debounce window must not silently discard
    // the tap — flush it instead of dropping it. The store and the toaster both
    // outlive this component, so the request is safe to finish unmounted.
    useEffect(
        () => () => {
            Object.keys(pending.current).forEach((stationId) => commit(stationId));
        },
        [commit]
    );

    const toggleFavourite = useCallback(
        (stationId, isFavourite) => {
            if (!stationId) return;
            const next = !isFavourite;
            const entry = pending.current[stationId];
            if (entry) clearTimeout(entry.timer);

            const { applyLocal: paint } = options.current;
            if (paint) paint(stationId, next);
            pending.current[stationId] = {
                baseline: entry ? entry.baseline : Boolean(isFavourite),
                desired: next,
                timer: setTimeout(() => commit(stationId), TOGGLE_DEBOUNCE_MS),
            };
        },
        [commit]
    );

    return { toggleFavourite };
};

export const useRefreshOnFocus = (onFocus) => {
    const callback = useRef(onFocus);
    callback.current = onFocus;

    useEffect(() => {
        const handleFocus = () => {
            if (document.visibilityState !== 'visible') return;
            if (callback.current) callback.current();
        };
        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleFocus);
        };
    }, []);
};

const PULL_THRESHOLD = 70; // pull needed to trigger a refresh
const PULL_MAX = 110; // how far the indicator can travel
const PULL_RESISTANCE = 0.5;
const PULL_SLOP = 8; // past this, the touch is a pull and not a tap

export const usePullToRefresh = (onRefresh) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startY = useRef(null);
    const dragged = useRef(false);

    const onTouchStart = useCallback(
        (event) => {
            dragged.current = false;
            if (refreshing || window.scrollY > 0) {
                startY.current = null;
                return;
            }
            startY.current = event.touches[0].clientY;
        },
        [refreshing]
    );

    const onTouchMove = useCallback((event) => {
        if (startY.current === null) return;
        const delta = event.touches[0].clientY - startY.current;
        if (delta > PULL_SLOP) dragged.current = true;
        setPullDistance(delta > 0 ? Math.min(delta * PULL_RESISTANCE, PULL_MAX) : 0);
    }, []);


    const onClickCapture = useCallback((event) => {
        if (!dragged.current) return;
        dragged.current = false;
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const onTouchEnd = useCallback(async () => {
        if (startY.current === null) return;
        const shouldRefresh = pullDistance >= PULL_THRESHOLD;
        startY.current = null;
        setPullDistance(0);
        if (!shouldRefresh || !onRefresh) return;
        setRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
        }
    }, [pullDistance, onRefresh]);

    const height = refreshing ? 44 : pullDistance;

    return {
        pullProps: {
            onTouchStart,
            onTouchMove,
            onTouchEnd,
            onClickCapture,
            // Keep the browser's own pull-to-refresh out of the way of ours.
            style: { overscrollBehaviorY: 'contain' },
        },
        // Styled inline so the hook carries no stylesheet of its own.
        pullIndicator: (
            <div
                style={{
                    height: `${height}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    transition: 'height 0.2s ease-out',
                }}
            >
                {height > 0 && <PreLoader width="32px" height="32px" />}
            </div>
        ),
    };
};
